import React from "react";

const CX = 500;
const CY = 560;
const OUTER_R = 490;
const INNER_R = 410;
const LABEL_R = 392;
const ENV_R = 468;
const SYS_R = 92;
const S1_R = 76;
const S1_Y = 812;

function polar(cx, cy, r, deg) {
  const a = (deg * Math.PI) / 180;
  return [cx + r * Math.cos(a), cy + r * Math.sin(a)];
}
function arc(cx, cy, r, s, e) {
  const [x1, y1] = polar(cx, cy, r, s);
  const [x2, y2] = polar(cx, cy, r, e);
  const large = Math.abs(e - s) > 180 ? 1 : 0;
  const sweep = e > s ? 1 : 0;
  return `M ${x1} ${y1} A ${r} ${r} 0 ${large} ${sweep} ${x2} ${y2}`;
}

function s1Positions(n) {
  if (n <= 0) return [];
  if (n === 1) return [500];
  if (n === 2) return [410, 590];
  return [335, 500, 665];
}

const RED = "#dc2626";

function wrapName(text, max) {
  const lines = [];
  let cur = "";
  for (const w of String(text || "").split(/\s+/).filter(Boolean)) {
    const next = cur ? `${cur} ${w}` : w;
    if (next.length <= max || !cur) cur = next;
    else { lines.push(cur); cur = w; }
  }
  if (cur) lines.push(cur);
  if (lines.length <= 2) return lines;
  const second = lines.slice(1).join(" ");
  return [lines[0], second.length > max ? second.slice(0, max - 1) + "…" : second];
}

function NameText({ x, y, text, max, fontSize, testid, alert = false }) {
  const lines = wrapName(text, max);
  const lh = fontSize * 1.1;
  const startY = y - ((lines.length - 1) * lh) / 2;
  return (
    <text x={x} y={startY} textAnchor="middle" className="font-grotesk" fontSize={lines.length > 1 ? fontSize - 2 : fontSize} fontWeight="700" fill={alert ? RED : "#09090b"} data-testid={testid} data-alert={alert}>
      {lines.map((l, i) => (
        <tspan key={i} x={x} dy={i === 0 ? 0 : lh}>{l}</tspan>
      ))}
    </text>
  );
}

function Arrow({ x, y, dir, testid, disabled, onClick }) {
  const d = dir === "left" ? `M ${x + 7} ${y - 11} L ${x - 7} ${y} L ${x + 7} ${y + 11}` : `M ${x - 7} ${y - 11} L ${x + 7} ${y} L ${x - 7} ${y + 11}`;
  return (
    <g
      className={`vsm-arrow ${disabled ? "disabled" : ""}`}
      data-testid={testid}
      onClick={disabled ? undefined : onClick}
    >
      <circle cx={x} cy={y} r="20" fill="#fff" stroke="#09090b" strokeWidth="1.5" />
      <path d={d} fill="none" stroke="#09090b" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </g>
  );
}

function UnitList({ cx, cy, r, units, selected, testid }) {
  const n = units.length;
  const shown = Math.min(n, 5);
  const start = Math.max(0, Math.min(selected - Math.floor(shown / 2), n - shown));
  const base = shown <= 2 ? 18 : shown === 3 ? 16 : 14;
  const lh = base * 1.3;
  const top = cy + 8 - ((shown - 1) * lh) / 2;
  return (
    <g data-testid={`${testid}-units`}>
      {start > 0 && <text x={cx} y={top - lh * 0.75} textAnchor="middle" fontSize="11" fill="#a1a1aa">…</text>}
      {units.slice(start, start + shown).map((u, i) => {
        const idx = start + i;
        const y = top + i * lh;
        const half = Math.sqrt(Math.max(r * r - (y - cy) ** 2, 0)) - 10;
        const fs = Math.min(base, (2 * half) / (Math.max(u.name.length, 1) * 0.58));
        const active = idx === selected;
        const alert = (u.skill_gap || []).length > 0;
        return (
          <text key={u.id} x={cx} y={y} textAnchor="middle" className="font-grotesk" fontSize={fs} fontWeight={active ? 700 : 400} fill={alert ? (active ? RED : "#f87171") : active ? "#09090b" : "#71717a"} data-testid={`${testid}-unit-${idx}`} data-active={active} data-alert={alert}>
            {alert && <title>{`Ontbrekende vaardigheden: ${u.skill_gap.join("; ")}`}</title>}
            {u.name}
          </text>
        );
      })}
      {start + shown < n && <text x={cx} y={top + (shown - 0.4) * lh} textAnchor="middle" fontSize="11" fill="#a1a1aa">…</text>}
    </g>
  );
}

function SystemCircle({ cx, cy, r, roman, sub, subAlert = false, units, selected = 0, count, testid, onClick }) {
  const hasUnits = Boolean(units && units.length > 1);
  const hasSub = Boolean(sub) || hasUnits;
  return (
    <g className="vsm-node" data-testid={testid} onClick={onClick}>
      <circle className="vsm-node-fill" cx={cx} cy={cy} r={r} fill="#fff" stroke="#09090b" strokeWidth="2" />
      <text x={cx} y={hasUnits ? cy - 62 : hasSub ? cy - 34 : cy - 6} textAnchor="middle" className="font-grotesk" fontSize="11" fontWeight="500" fill="#71717a">
        {hasUnits ? `System ${roman}` : "System"}
      </text>
      {!hasUnits && (
        <text x={cx} y={hasSub ? cy - 20 : cy + 10} textAnchor="middle" className="font-grotesk" fontSize="13" fontWeight="600" fill="#71717a">
          {roman}
        </text>
      )}
      {hasUnits && <UnitList cx={cx} cy={cy} r={r} units={units} selected={selected} testid={testid} />}
      {hasSub && !hasUnits && (
        <NameText x={cx} y={cy + 20} text={sub} max={13} fontSize={21} testid={`${testid}-name`} alert={subAlert} />
      )}
      {typeof count === "number" && (
        <g>
          <circle cx={cx + r - 14} cy={cy - r + 14} r="15" fill="#18181b" />
          <text x={cx + r - 14} y={cy - r + 19} textAnchor="middle" className="font-grotesk" fontSize="13" fontWeight="700" fill="#fff">
            {count}
          </text>
        </g>
      )}
    </g>
  );
}

export default function CircumplexDiagram({
  detail,
  canEdit,
  sys1Start,
  onSys1Prev,
  onSys1Next,
  onZoomSys1,
  sys2Index,
  onSys2Prev,
  onSys2Next,
  sys4Index,
  onSys4Prev,
  onSys4Next,
  onOpenSystem,
  onAddSys1,
}) {
  const node = detail.node;
  const s1 = detail.system1_children;
  const s2units = node.system2_units || [];
  const s4units = node.system4_units || [];
  const s2unit = s2units[sys2Index] || null;
  const s4unit = s4units[sys4Index] || null;

  const visible = s1.slice(sys1Start, sys1Start + 3);
  const positions = s1Positions(visible.length);

  const sVxy = [CX, 300];
  const sIVxy = [262, CY];
  const sIIxy = [CX, CY];
  const sIIIxy = [738, CY];

  return (
    <svg viewBox="0 0 1000 1120" className="w-full h-auto select-none" data-testid="circumplex-diagram-svg">
      <defs>
        <path id="envPath" d={arc(CX, CY, ENV_R, 212, 328)} />
        <path id="topPath" d={arc(CX, CY, LABEL_R, 232, 308)} />
        <path id="botPath" d={arc(CX, CY, LABEL_R, 148, 32)} />
        <path id="leftPath" d={arc(CX, CY, LABEL_R, 148, 212)} />
        <path id="rightPath" d={arc(CX, CY, LABEL_R, 328, 392)} />
      </defs>

      {/* rings */}
      <circle cx={CX} cy={CY} r={OUTER_R} fill="none" stroke="#09090b" strokeWidth="2" />
      <circle cx={CX} cy={CY} r={INNER_R} fill="none" stroke="#09090b" strokeWidth="1.5" />

      {/* curved labels */}
      <text className="font-grotesk" fontSize="30" fontWeight="500" letterSpacing="9" fill="#27272a">
        <textPath href="#envPath" startOffset="50%" textAnchor="middle">
          ENVIRONMENT
        </textPath>
      </text>
      <text className="font-grotesk" fontSize="30" fontWeight="700" letterSpacing="1" fill="#09090b">
        <textPath href="#topPath" startOffset="50%" textAnchor="middle">
          Social Stability
        </textPath>
      </text>
      <text className="font-grotesk" fontSize="30" fontWeight="700" letterSpacing="1" fill="#09090b">
        <textPath href="#botPath" startOffset="50%" textAnchor="middle">
          Autonomy
        </textPath>
      </text>
      <text className="font-grotesk" fontSize="30" fontWeight="700" letterSpacing="1" fill="#09090b">
        <textPath href="#leftPath" startOffset="50%" textAnchor="middle">
          Exploration
        </textPath>
      </text>
      <text className="font-grotesk" fontSize="30" fontWeight="700" letterSpacing="1" fill="#09090b">
        <textPath href="#rightPath" startOffset="50%" textAnchor="middle">
          Exploitation
        </textPath>
      </text>

      {/* connections */}
      <g stroke="#09090b" strokeWidth="1.5" fill="none">
        <line x1={sVxy[0]} y1={sVxy[1]} x2={sIVxy[0]} y2={sIVxy[1]} />
        <line x1={sVxy[0]} y1={sVxy[1]} x2={sIIxy[0]} y2={sIIxy[1]} />
        <line x1={sVxy[0]} y1={sVxy[1]} x2={sIIIxy[0]} y2={sIIIxy[1]} />
        <line x1={sIVxy[0]} y1={sIVxy[1]} x2={sIIxy[0]} y2={sIIxy[1]} />
        <line x1={sIIxy[0]} y1={sIIxy[1]} x2={sIIIxy[0]} y2={sIIIxy[1]} />
        {positions.map((x, i) => (
          <g key={i}>
            <line x1={sIIxy[0]} y1={sIIxy[1]} x2={x} y2={S1_Y} />
            <line x1={sIIIxy[0]} y1={sIIIxy[1]} x2={x} y2={S1_Y} />
          </g>
        ))}
      </g>

      {/* System V */}
      <SystemCircle
        cx={sVxy[0]}
        cy={sVxy[1]}
        r={SYS_R}
        roman="V"
        sub={node.system5_name}
        count={node.system5_employees.length}
        testid="system-5-node"
        onClick={() => onOpenSystem("s5", null)}
      />

      {/* System IV with unit carousel */}
      <SystemCircle
        cx={sIVxy[0]}
        cy={sIVxy[1]}
        r={SYS_R}
        roman="IV"
        sub={s4unit ? s4unit.name : "geen afdeling"}
        subAlert={(s4unit?.skill_gap || []).length > 0}
        units={s4units}
        selected={sys4Index}
        count={s4unit ? s4unit.employees.length : 0}
        testid="system-4-node"
        onClick={() => onOpenSystem("s4", s4unit ? s4unit.id : null)}
      />
      {s4units.length > 1 && (
        <>
          <Arrow x={sIVxy[0] - 72} y={sIVxy[1] + SYS_R + 22} dir="left" testid="sys4-prev-btn" onClick={onSys4Prev} />
          <Arrow x={sIVxy[0] + 72} y={sIVxy[1] + SYS_R + 22} dir="right" testid="sys4-next-btn" onClick={onSys4Next} />
        </>
      )}
      {s4units.length > 0 && (
        <text x={sIVxy[0]} y={sIVxy[1] + SYS_R + 46} textAnchor="middle" className="font-mono" fontSize="12" fill="#52525b" data-testid="sys4-counter">
          {`afdeling ${sys4Index + 1} / ${s4units.length}`}
        </text>
      )}

      {/* System II with unit carousel */}
      <SystemCircle
        cx={sIIxy[0]}
        cy={sIIxy[1]}
        r={SYS_R}
        roman="II"
        sub={s2unit ? s2unit.name : "geen afdeling"}
        subAlert={(s2unit?.skill_gap || []).length > 0}
        units={s2units}
        selected={sys2Index}
        count={s2unit ? s2unit.employees.length : 0}
        testid="system-2-node"
        onClick={() => onOpenSystem("s2", s2unit ? s2unit.id : null)}
      />
      {s2units.length > 1 && (
        <>
          <Arrow x={sIIxy[0] - 72} y={sIIxy[1] + SYS_R + 22} dir="left" testid="sys2-prev-btn" onClick={onSys2Prev} />
          <Arrow x={sIIxy[0] + 72} y={sIIxy[1] + SYS_R + 22} dir="right" testid="sys2-next-btn" onClick={onSys2Next} />
        </>
      )}
      {s2units.length > 0 && (
        <text x={sIIxy[0]} y={sIIxy[1] + SYS_R + 46} textAnchor="middle" className="font-mono" fontSize="12" fill="#52525b" data-testid="sys2-counter">
          {`afdeling ${sys2Index + 1} / ${s2units.length}`}
        </text>
      )}

      {/* System III */}
      <SystemCircle
        cx={sIIIxy[0]}
        cy={sIIIxy[1]}
        r={SYS_R}
        roman="III"
        sub={node.system3_name}
        count={node.system3_employees.length}
        testid="system-3-node"
        onClick={() => onOpenSystem("s3", null)}
      />

      {/* System I carousel */}
      {visible.map((c, i) => {
        const x = positions[i];
        return (
          <g key={c.id} className="vsm-node" data-testid={`system-1-node-${sys1Start + i}`} onClick={() => onZoomSys1(c)}>
            <circle className="vsm-node-fill" cx={x} cy={S1_Y} r={S1_R} fill="#fff" stroke="#09090b" strokeWidth="2" />
            {!c.is_individual && (
              <g data-testid={`system-1-count-${sys1Start + i}`}>
                <title>{`${c.employee_count} medewerkers in onderliggende systemen`}</title>
                <circle cx={x + S1_R - 12} cy={S1_Y - S1_R + 12} r="15" fill="#18181b" />
                <text x={x + S1_R - 12} y={S1_Y - S1_R + 17} textAnchor="middle" className="font-grotesk" fontSize="13" fontWeight="700" fill="#fff">
                  {c.employee_count}
                </text>
              </g>
            )}
            <text x={x} y={S1_Y - 38} textAnchor="middle" className="font-grotesk" fontSize="11" fontWeight="500" fill="#71717a">
              System I
            </text>
            {(c.skill_gap || []).length > 0 && <title>{`Ontbrekende vaardigheden: ${c.skill_gap.join("; ")}`}</title>}
            <NameText x={x} y={S1_Y} text={c.employee ? c.employee.name : c.name} max={12} fontSize={18} testid={`system-1-name-${sys1Start + i}`} alert={(c.skill_gap || []).length > 0} />
            <text x={x} y={S1_Y + 34} textAnchor="middle" className="font-mono" fontSize="10" fill="#52525b">
              {c.is_individual ? "medewerker" : `${c.child_count} sub · zoom in`}
            </text>
          </g>
        );
      })}
      {visible.length === 0 && (
        <text x={CX} y={S1_Y} textAnchor="middle" className="font-mono" fontSize="14" fill="#a1a1aa" data-testid="sys1-empty">
          Nog geen Systemen I
        </text>
      )}

      {s1.length > 3 && (
        <>
          <Arrow x={150} y={S1_Y} dir="left" testid="sys1-prev-btn" disabled={sys1Start <= 0} onClick={onSys1Prev} />
          <Arrow x={850} y={S1_Y} dir="right" testid="sys1-next-btn" disabled={sys1Start + 3 >= s1.length} onClick={onSys1Next} />
        </>
      )}
      {s1.length > 0 && (
        <text x={CX} y={S1_Y + S1_R + 28} textAnchor="middle" className="font-mono" fontSize="12" fill="#52525b" data-testid="sys1-counter">
          {`Systeem I ${Math.min(sys1Start + 1, s1.length)}–${Math.min(sys1Start + 3, s1.length)} / ${s1.length}`}
        </text>
      )}

      {canEdit && !node.is_individual && (
        <g className="vsm-arrow" data-testid="sys1-add-btn" onClick={onAddSys1}>
          <circle cx={CX} cy={1000} r="26" fill="#fff" stroke="#09090b" strokeWidth="1.5" strokeDasharray="4 3" />
          <path d={`M ${CX - 11} 1000 L ${CX + 11} 1000 M ${CX} 989 L ${CX} 1011`} stroke="#09090b" strokeWidth="2.4" strokeLinecap="round" />
          <text x={CX} y={1045} textAnchor="middle" className="font-mono" fontSize="12" fill="#52525b">
            Systeem I toevoegen
          </text>
        </g>
      )}
    </svg>
  );
}
